import React from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { makeStyles } from "@material-ui/core/styles";
import Security from "@material-ui/icons/Security";
import PeopleOutline from "@material-ui/icons/PeopleOutline";
import SettingsOutlined from "@material-ui/icons/SettingsOutlined";
import NotificationsNoneOutlined from "@material-ui/icons/NotificationsNoneOutlined";
import ChatBubbleOutline from "@material-ui/icons/ChatBubbleOutline";
import Restore from "@material-ui/icons/Restore";
import SaveOutlined from "@material-ui/icons/SaveOutlined";
import FarApi from "../../../../assets/js/utils/far/farApi";
import { FAR_DEFAULT_MESSAGE, FAR_ENFORCEMENT, formatFarMessage } from "../../../../assets/js/utils/far/farEngine";
import farPageStyle from "../../../../assets/jss/user/far/farPageStyle.jsx";
import { FarNotice } from "./farUi";
import LoaderComponent from "../../Loader";

const useStyles = makeStyles(theme => farPageStyle(theme));

function Radio({ classes, name, value, current, onChange, title, hint, text, disabled }){
    const id = "far-set-"+name+"-"+value;
    return (
        <label className={classes.option} htmlFor={id}>
            <input type="radio" id={id} name={name} value={value} checked={current === value} onChange={() => onChange(name, value)} disabled={disabled} />
            <span>
                <span className={classes.optionTitle}>{title}{ hint ? <em> {hint}</em> : null }</span>
                <span className={classes.optionText}>{text}</span>
            </span>
        </label>
    );
}

function Check({ classes, name, current, onChange, title, text, disabled }){
    const id = "far-set-"+name;
    return (
        <label className={classes.option} htmlFor={id}>
            <input type="checkbox" id={id} checked={!!current} onChange={(e) => onChange(name, e.target.checked)} disabled={disabled} />
            <span>
                <span className={classes.optionTitle}>{title}</span>
                <span className={classes.optionText}>{text}</span>
            </span>
        </label>
    );
}

function FarSettingsTab({ canManage, onSaved }){
    const classes = useStyles();
    const [settings, setSettings] = React.useState(null);
    const [saved, setSaved] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [loadError, setLoadError] = React.useState(null);
    const [saving, setSaving] = React.useState(false);
    const [notice, setNotice] = React.useState(null);
    const sourceRef = React.useRef(null);

    const load = React.useCallback(() => {
        const source = axios.CancelToken.source();
        sourceRef.current = source;
        setLoading(true);
        setLoadError(null);
        FarApi.getSettings(source).then(data => {
            setSettings(data);
            setSaved(data);
            setLoading(false);
        }).catch(err => {
            if(err && err.message === "Request Cancelled"){ return; }
            setLoading(false);
            setLoadError((err && err.message) || "Settings could not be loaded.");
        });
    }, []);

    React.useEffect(() => {
        load();
        return () => { if(sourceRef.current){ sourceRef.current.cancel("Request Cancelled"); } };
    }, [load]);

    if(loading){
        return <LoaderComponent color="custom" align="center" />;
    }
    if(loadError){
        return <FarNotice tone="danger" action={<button type="button" className={classes.secondaryButton} onClick={load}>Retry</button>}>{loadError}</FarNotice>;
    }

    const disabled = !canManage || saving;
    const dirty = JSON.stringify(settings) !== JSON.stringify(saved);
    const set = (name, value) => {
        setNotice(null);
        setSettings(prev => Object.assign({}, prev, { [name]: value }));
    };

    const save = () => {
        if(disabled){ return; }
        setSaving(true);
        setNotice(null);
        const payload = Object.assign({}, settings, {
            default_message: (settings.default_message || "").trim().length > 0 ? settings.default_message : FAR_DEFAULT_MESSAGE
        });
        FarApi.saveSettings(payload).then(() => {
            setSaving(false);
            setSettings(payload);
            setSaved(payload);
            setNotice({ tone: "success", text: "Settings saved. They apply to new sends right away." });
            if(onSaved){ onSaved(payload); }
        }).catch(err => {
            setSaving(false);
            // Keep the user's edits; nothing was saved on the server.
            setNotice({ tone: "danger", text: ((err && err.message) || "Settings could not be saved.")+" Your changes have not been applied." });
        });
    };

    const preview = formatFarMessage(settings.default_message, { file: "Inv-637_abc.pdf", rule: "ABC Inc Invoices", recipients: "ap@abcinc.com" });

    return (
        <div className={classes.main}>
            { !canManage ? <FarNotice tone="info">Only account administrators can change FAR settings.</FarNotice> : null }
            { notice ? <FarNotice tone={notice.tone}>{notice.text}</FarNotice> : null }
            <div className={classes.settingsGrid}>
                <section className={classes.settingsCard} aria-labelledby="far-set-enforcement">
                    <div className={classes.settingsCardHead}>
                        <Security aria-hidden="true" />
                        <div>
                            <h3 className={classes.settingsTitle} id="far-set-enforcement">Rule Enforcement</h3>
                            <p className={classes.settingsText}>Choose what happens when a file matches a rule but the recipient is not authorized.</p>
                        </div>
                    </div>
                    <fieldset className={classes.fieldset}>
                        <legend className={classes.srOnly}>Rule enforcement</legend>
                        <Radio classes={classes} name="enforcement" value={FAR_ENFORCEMENT.WARN_REQUIRE_AUTH} current={settings.enforcement} onChange={set} disabled={disabled}
                            title="Warn and require authorization" hint="(recommended)" text="Show a warning and require the user to authorize or resolve before sending." />
                        <Radio classes={classes} name="enforcement" value={FAR_ENFORCEMENT.BLOCK} current={settings.enforcement} onChange={set} disabled={disabled}
                            title="Block sending" text="Prevent the transfer from being sent. Only administrators can override." />
                        <Radio classes={classes} name="enforcement" value={FAR_ENFORCEMENT.WARN_ONLY} current={settings.enforcement} onChange={set} disabled={disabled}
                            title="Warn only" text="Show a warning but allow the user to send after acknowledgement." />
                    </fieldset>
                </section>

                <section className={classes.settingsCard} aria-labelledby="far-set-auth">
                    <div className={classes.settingsCardHead}>
                        <PeopleOutline aria-hidden="true" />
                        <div>
                            <h3 className={classes.settingsTitle} id="far-set-auth">Authorization &amp; Overrides</h3>
                            <p className={classes.settingsText}>Control who can authorize exceptions and how they are handled.</p>
                        </div>
                    </div>
                    <fieldset className={classes.fieldset}>
                        <legend className={classes.srOnly}>Who can authorize exceptions</legend>
                        <Radio classes={classes} name="override_permission" value="any" current={settings.override_permission} onChange={set} disabled={disabled || settings.enforcement === FAR_ENFORCEMENT.BLOCK}
                            title="Allow any user to authorize" hint="(default)" text="Users can authorize and continue after a warning." />
                        <Radio classes={classes} name="override_permission" value="admins" current={settings.enforcement === FAR_ENFORCEMENT.BLOCK ? "admins" : settings.override_permission} onChange={set} disabled={disabled || settings.enforcement === FAR_ENFORCEMENT.BLOCK}
                            title="Administrators only" text="Only administrators can authorize exceptions." />
                    </fieldset>
                    <Check classes={classes} name="require_override_reason" current={settings.require_override_reason} onChange={set} disabled={disabled}
                        title="Require an override reason" text="Users must enter a reason when authorizing an exception." />
                    <Check classes={classes} name="log_overrides" current={settings.log_overrides} onChange={set} disabled={disabled}
                        title="Log all overrides to Audit Log" text="Keep a record of all authorized exceptions." />
                    <Check classes={classes} name="notify_admins" current={settings.notify_admins} onChange={set} disabled={disabled}
                        title="Notify administrators on overrides" text="Send an email notification when a user authorizes an exception." />
                </section>

                <section className={classes.settingsCard} aria-labelledby="far-set-processing">
                    <div className={classes.settingsCardHead}>
                        <SettingsOutlined aria-hidden="true" />
                        <div>
                            <h3 className={classes.settingsTitle} id="far-set-processing">Rule Processing</h3>
                            <p className={classes.settingsText}>Configure how files are checked against your rules.</p>
                        </div>
                    </div>
                    <Check classes={classes} name="real_time" current={settings.real_time} onChange={set} disabled={disabled}
                        title="Apply rules in real time" text="Check files and recipients immediately as they are added." />
                    <Check classes={classes} name="case_insensitive" current={settings.case_insensitive} onChange={set} disabled={disabled}
                        title="Case-insensitive filename matching" text="Treat file name prefix and suffix as case-insensitive (e.g. INV = inv)." />
                    <fieldset className={classes.fieldset}>
                        <legend className={classes.srOnly}>When a file matches more than one rule</legend>
                        <Radio classes={classes} name="match_mode" value="all" current={settings.match_mode} onChange={set} disabled={disabled}
                            title="Enforce all matching rules" text="If a file matches multiple rules, all must be satisfied." />
                        <Radio classes={classes} name="match_mode" value="first" current={settings.match_mode} onChange={set} disabled={disabled}
                            title="Use first matching rule only" text="Stop checking after the first matching rule is found." />
                    </fieldset>
                    <Check classes={classes} name="include_compressed" current={settings.include_compressed} onChange={set} disabled={disabled}
                        title="Include compressed files (ZIP, RAR, etc.)" text="Also check files inside compressed archives. Applied by the Outlook plug-in and the server at send time." />
                </section>

                <section className={classes.settingsCard} aria-labelledby="far-set-ux">
                    <div className={classes.settingsCardHead}>
                        <NotificationsNoneOutlined aria-hidden="true" />
                        <div>
                            <h3 className={classes.settingsTitle} id="far-set-ux">Notifications &amp; User Experience</h3>
                            <p className={classes.settingsText}>Control what information is shown to users when a rule is triggered.</p>
                        </div>
                    </div>
                    <Check classes={classes} name="show_status_banner" current={settings.show_status_banner} onChange={set} disabled={disabled}
                        title="Show FAR status on Send page" text="Display whether File Attachment Rules are active." />
                    <Check classes={classes} name="show_rule_details" current={settings.show_rule_details} onChange={set} disabled={disabled}
                        title="Show matching rule details" text="Display the rule name and match criteria to the user." />
                    <Check classes={classes} name="show_authorized_recipients" current={settings.show_authorized_recipients} onChange={set} disabled={disabled}
                        title="Show authorized recipient(s)" text="Display the allowed email addresses when a rule is triggered." />
                    <Check classes={classes} name="highlight_invalid_recipients" current={settings.highlight_invalid_recipients} onChange={set} disabled={disabled}
                        title="Highlight invalid recipients" text="Mark recipients that are not authorized with a red warning icon." />
                    <Check classes={classes} name="allow_ignore" current={settings.allow_ignore} onChange={set} disabled={disabled || settings.enforcement === FAR_ENFORCEMENT.BLOCK}
                        title={"Allow \"Ignore and Continue\" option"} text="Let users ignore the warning after explicit confirmation (if permitted by the enforcement setting). Always logged." />
                    <Check classes={classes} name="remember_override_reason" current={settings.remember_override_reason} onChange={set} disabled={disabled}
                        title="Remember last used override reason" text="Pre-fill the last override reason on this browser for convenience." />
                </section>

                <section className={classes.messageCard} aria-labelledby="far-set-message">
                    <div className={classes.settingsCardHead}>
                        <ChatBubbleOutline aria-hidden="true" />
                        <div style={{flex: 1}}>
                            <h3 className={classes.settingsTitle} id="far-set-message">Default Message <span style={{fontWeight: 400}}>(Optional)</span></h3>
                            <p className={classes.settingsText}>Customize the message shown to users when a rule is triggered.</p>
                        </div>
                        <button type="button" className={classes.cancelButton} onClick={() => set("default_message", FAR_DEFAULT_MESSAGE)} disabled={disabled}>
                            <Restore aria-hidden="true" style={{width: 18, height: 18}} />Reset to Default
                        </button>
                    </div>
                    <label htmlFor="far-set-default-message" className={classes.srOnly}>Default message</label>
                    <textarea id="far-set-default-message" className={classes.textarea} value={settings.default_message} maxLength={1000}
                        onChange={(e) => set("default_message", e.target.value)} disabled={disabled} aria-describedby="far-set-default-message-help" />
                    <p className={classes.placeholders} id="far-set-default-message-help">
                        Placeholders: <code>{"{file}"}</code> <code>{"{rule}"}</code> <code>{"{recipients}"}</code>. Shown as plain text.
                    </p>
                    <div className={classes.preview} aria-label="Message preview">{preview}</div>
                    <div className={classes.rowEnd}>
                        { dirty && canManage ? <button type="button" className={classes.cancelButton} onClick={() => { setSettings(saved); setNotice(null); }} disabled={saving}>Discard changes</button> : null }
                        <button type="button" className={classes.accentButton} onClick={save} disabled={disabled || !dirty}>
                            { saving ? <LoaderComponent color="white" saving={true} /> : <><SaveOutlined aria-hidden="true" style={{width: 18, height: 18}} />Save Settings</> }
                        </button>
                    </div>
                </section>
            </div>
        </div>
    );
}

FarSettingsTab.defaultProps = { canManage: true, onSaved: null };
FarSettingsTab.propTypes = { canManage: PropTypes.bool, onSaved: PropTypes.func };

export default FarSettingsTab;
